<template>
    <!-- Renders nothing when the rail has neither related tours nor an
         admin-selected content block, so the post layout collapses back to a
         single column (the rail "hides" itself). -->
    <aside v-if="resolved.length || contentBlockId"
        class="related-tours p-4 lg:p-5 lg:sticky topbar-offset">
        <template v-if="resolved.length">
            <p class="text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-primary m-0 mb-1">
                Keep exploring
            </p>
            <h2 class="text-lg font-bold leading-tight mb-4">
                {{ computedHeading }}
            </h2>

            <Tours :tours="resolved" layout="rail" />
        </template>

        <!-- Admin-configured content block, shown below the tours (or on its
             own when the post has no related tours). -->
        <content-block v-if="contentBlockId" :id="contentBlockId"
            :class="resolved.length ? 'block mt-6 pt-6 border-t border-neutral-200' : ''" />

    </aside>
</template>

<script setup lang="ts">
import { Tour } from "@/types";
import { computed } from "vue";
import Tours from "@/Components/Tour/Tours.vue";
import ContentBlock from "@/Components/ContentBlock.vue";

// `tours` is passed by the post chrome (Page/Show.vue); `ssrData.tours` is the
// server-resolved payload when used as the `[related-tours]` CMS shortcode.
const props = defineProps<{
    tours?: Tour[];
    // Explicit override; when absent the heading is derived from the tours.
    heading?: string;
    // Admin-selected content block rendered in the rail (inspiration posts).
    contentBlockId?: number | null;
    ssrData?: { tours?: Tour[] };
    // How many tours the rail is capped at — lets us tell whether the rail is
    // already showing the complete related set (nothing more to browse).
    // `take` is the shortcode attribute alias for the same limit.
    cap?: number;
    take?: number | string;
}>();

const resolved = computed<Tour[]>(() => props.tours ?? props.ssrData?.tours ?? []);

const computedHeading = computed<string>(() => {
    if (props.heading) return props.heading;
    return "Turn this story into your next trip";
});
</script>
